// Write-only telemetry for failed browser → R2 uploads.
//
// Upload drops otherwise leave nothing behind but a toast, so support has no
// way to tell an expired signed URL apart from a genuine connection drop.
// Every insert is fire-and-forget: logging must never break an upload.

import { supabase } from "@/integrations/supabase/client";

export interface UploadFailureLogEntry {
  bucket: string;
  path: string;
  fileSizeBytes?: number;
  contentType?: string;
  /** "single" for one PUT, "multipart" for chunked uploads. */
  mode: "single" | "multipart";
  partNumber?: number;
  attempt: number;
  /** True for the row written when all retries are exhausted. */
  isFinal: boolean;
  errorMessage: string;
  httpStatus?: number;
  loadedBytes?: number;
  elapsedMs?: number;
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

function connectionInfo(): NetworkInformation | null {
  const conn = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!conn) return null;
  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
    saveData: conn.saveData,
    type: conn.type,
  };
}

export function logUploadFailure(entry: UploadFailureLogEntry): void {
  void (async () => {
    try {
      const { data } = await supabase.auth.getUser();
      await (supabase as unknown as { from: (t: string) => { insert: (v: Record<string, unknown>) => Promise<unknown> } })
        .from("upload_failure_logs")
        .insert({
        user_id: data.user?.id ?? null,
        bucket: entry.bucket,
        path: entry.path,
        file_size_bytes: entry.fileSizeBytes ?? null,
        content_type: entry.contentType ?? null,
        mode: entry.mode,
        part_number: entry.partNumber ?? null,
        attempt: entry.attempt,
        is_final: entry.isFinal,
        error_message: entry.errorMessage.slice(0, 2000),
        http_status: entry.httpStatus ?? null,
        loaded_bytes: entry.loadedBytes ?? null,
        elapsed_ms: entry.elapsedMs ?? null,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        connection: connectionInfo(),
      });
    } catch {
      /* telemetry only — never surface logging failures to the user */
    }
  })();
}
